/**
 * Permission Utilities
 * Helper functions for role-based access control
 *
 * NOTE: This file provides Role[]-centric helpers (takes Role[]).
 * For User-centric helpers (takes User | null), see ./rbac.ts.
 * Both files share the same role constants and scope logic.
 */

import { Role } from '../contexts/AuthContext';

export type RoleScope = 'platform' | 'organization' | 'site' | 'zone';

const SCOPED_OPERATION_ROLE_NAMES = [
  'مشرف قسم',
  'Zone Supervisor',
  'مدير المستشفى',
  'Hospital Manager',
  'فني صيانة',
  'Technician',
  'operator',
  'Operator',
  'viewer',
  'Viewer',
];

/**
 * Check if user has a specific role by name
 */
export const hasRole = (userRoles: Role[], roleName: string): boolean => {
  return userRoles.some((role) => role.name === roleName || role.nameAr === roleName);
};

/**
 * Check if user has a specific scope
 */
export const hasScope = (userRoles: Role[], scope: RoleScope): boolean => {
  return userRoles.some((role) => role.scope === scope);
};

const isScopedOperationsRole = (role: Role): boolean => {
  return (
    SCOPED_OPERATION_ROLE_NAMES.includes(role.name) ||
    SCOPED_OPERATION_ROLE_NAMES.includes(role.nameAr) ||
    role.scope === 'zone' ||
    role.scope === 'site'
  );
};

/**
 * Get user's highest scope (platform > organization > site > zone)
 */
export const getHighestScope = (userRoles: Role[]): RoleScope | null => {
  if (!userRoles || userRoles.length === 0) return null;

  const scopeOrder: RoleScope[] = ['platform', 'organization', 'site', 'zone'];

  for (const scope of scopeOrder) {
    if (userRoles.some((role) => role.scope === scope)) {
      return scope;
    }
  }

  return null;
};

/**
 * Check if user is Platform Admin
 */
export const isPlatformAdmin = (userRoles: Role[]): boolean => {
  return hasScope(userRoles, 'platform');
};

/**
 * Check if user is Organization Admin
 * Matches web's isOrgAdminAccess — does NOT include platform scope.
 * Use isPlatformAdmin() || isOrganizationAdmin() for combined access.
 */
export const isOrganizationAdmin = (userRoles: Role[]): boolean => {
  return userRoles.some((role) => {
    if (isScopedOperationsRole(role)) {
      return false;
    }

    return (
      role.scope === 'organization' ||
      role.name === 'مدير النظام' ||
      role.name === 'Organization Admin' ||
      role.name === 'Admin' ||
      role.name === 'Demo Admin'
    );
  });
};

/**
 * Check if user is Site Manager or higher
 */
export const isSiteManager = (userRoles: Role[]): boolean => {
  return (
    hasScope(userRoles, 'site') || hasScope(userRoles, 'organization') || isPlatformAdmin(userRoles)
  );
};

/**
 * Check if user can manage devices (create, edit, delete)
 * Platform admin + Org admin + all zone/site-level roles (فني صيانة, مشرف قسم, مدير المستشفى)
 */
export const canManageDevices = (userRoles: Role[]): boolean => {
  return (
    isPlatformAdmin(userRoles) ||
    isOrganizationAdmin(userRoles) ||
    hasScope(userRoles, 'site') ||
    hasScope(userRoles, 'zone')
  );
};

/**
 * Check if user can manage users
 */
export const canManageUsers = (userRoles: Role[]): boolean => {
  return isPlatformAdmin(userRoles) || isOrganizationAdmin(userRoles);
};

/**
 * Check if user can manage sites
 */
export const canManageSites = (userRoles: Role[]): boolean => {
  return isPlatformAdmin(userRoles) || isOrganizationAdmin(userRoles);
};

/**
 * Check if user can manage zones
 */
export const canManageZones = (userRoles: Role[]): boolean => {
  return isPlatformAdmin(userRoles) || isOrganizationAdmin(userRoles);
};

/**
 * Check if user can manage zone permissions
 */
export const canManageZonePermissions = (userRoles: Role[]): boolean => {
  return isPlatformAdmin(userRoles) || isOrganizationAdmin(userRoles);
};

/**
 * Check if user can access organization-admin dashboard/management screens.
 * Mirrors web's hasOrgAdminDashboardAccess: platform admin OR org admin.
 */
export const canAccessOrgAdminScreens = (userRoles: Role[]): boolean => {
  return isPlatformAdmin(userRoles) || isOrganizationAdmin(userRoles);
};

/**
 * Check if user can view reports
 */
export const canViewReports = (userRoles: Role[]): boolean => {
  return true; // All authenticated users can view reports
};

/**
 * Check if user can acknowledge alarms
 */
export const canAcknowledgeAlarms = (userRoles: Role[]): boolean => {
  return true; // All authenticated users can acknowledge alarms
};

/**
 * Check if user can resolve alarms
 * Only platform admin, org admin, and مدير المستشفى have alarm:resolve in DB
 */
export const canResolveAlarms = (userRoles: Role[]): boolean => {
  return (
    isPlatformAdmin(userRoles) ||
    isOrganizationAdmin(userRoles) ||
    hasRole(userRoles, 'مدير المستشفى')
  );
};

/**
 * Get user's role display name (Arabic or English)
 */
export const getRoleDisplayName = (role: Role, language: 'ar' | 'en' = 'ar'): string => {
  return language === 'ar' ? role.nameAr : role.name;
};

/**
 * Get user's scope display name (Arabic or English)
 */
export const getScopeDisplayName = (scope: RoleScope, language: 'ar' | 'en' = 'ar'): string => {
  const translations = {
    platform: { ar: 'منصة', en: 'Platform' },
    organization: { ar: 'منظمة', en: 'Organization' },
    site: { ar: 'موقع', en: 'Site' },
    zone: { ar: 'منطقة', en: 'Zone' },
  };

  return translations[scope][language];
};
